import { memo } from 'react';
import { useRouter } from 'next/router';
import { ICaseBattleGame } from '@/utils/types/ICaseBattle';
import { getMaxPlayers } from '@/utils/players';
import useUserStore from '@/stores/user.store';
import { modalStore } from '@/stores/modal.store';
import { login } from '@/utils/auth';
import GameBattleCasesList from '@/components/CaseBattles/Game/GameBattleCasesList';
import BasePlayers from '@/components/CaseBattles/BasePlayers';
import GameBattleAvatarPlaceholder from '@/components/CaseBattles/Game/Avatar/GameBattleAvatarPlaceholder';

interface Props {
    game: ICaseBattleGame;
}

const STATUS_META = {
    PENDING: { label: 'Open', color: '#6ee7b7' },
    STARTED: { label: 'Playing', color: '#ffb13d' },
    FINISHED: { label: 'Ended', color: 'rgba(245,246,248,0.30)' },
} as const;

const GameBattle = memo(({ game }: Props) => {
    const router = useRouter();
    const { user, isAuthed } = useUserStore((state) => state);
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);

    const maxPlayers = getMaxPlayers(game.type);
    const status = STATUS_META[game.status];
    const players = game.teams.flatMap((team) => team.participants);
    const joined = players.some((p) => p.userId === user.id);

    const isTwoVsTwo = game.type === 'TWO_VS_TWO';
    const cursed = game.typeWinning === 'WINS_LOWEST';
    const modeColor = cursed ? '#46e08a' : '#ffc24d';
    const modeLabel = `${cursed ? 'Cursed ' : ''}${isTwoVsTwo ? '2v2' : `${maxPlayers}-way`}`;

    const totalRounds = game.cases.length;
    const openedRounds = Math.min(
        game.teams[0]?.participants[0]?.rolls?.length ?? 0,
        totalRounds
    );

    const join = () => {
        setTitle('Join battle');
        setComponent('Join battle');
        setData({ id: game.id });
        toggleModal();
    };

    const goView = () => router.push(`/casebattles/${game.id}`);
    const goReplay = () => router.push(`/casebattles/${game.id}?replay=true`);

    const renderAction = () => {
        if (!isAuthed) {
            return <ActionButton onClick={login}>Login</ActionButton>;
        }
        switch (game.status) {
            case 'PENDING':
                return joined ? (
                    <ActionButton onClick={goView}>View</ActionButton>
                ) : (
                    <ActionButton accent onClick={join}>
                        Join
                    </ActionButton>
                );
            case 'STARTED':
                return <ActionButton onClick={goView}>Playing</ActionButton>;
            case 'FINISHED':
                return <ActionButton onClick={goReplay}>Replay</ActionButton>;
        }
    };

    return (
        <div
            onClick={goView}
            className={`relative bg-layer_1 border border-stroke rounded-fl-md hover:bg-layer_2 hover:border-stroke_strong cursor-pointer transition-all
                grid items-center gap-4 px-4 py-3
                grid-cols-[minmax(230px,260px)_minmax(0,1fr)_auto]
                max-2xl:grid-cols-[220px_minmax(0,1fr)_auto]
                max-md:!flex max-md:!flex-col max-md:items-stretch max-md:gap-3 max-md:p-3.5
                ${game.status === 'FINISHED' ? 'opacity-85' : ''}`}
        >
            <span
                className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r"
                style={{
                    background: status.color,
                    boxShadow: game.status === 'STARTED' ? `0 0 10px ${status.color}` : undefined,
                }}
            />

            {/* LEFT — amount + mode (top) and player avatars (bottom) */}
            <div className="flex flex-col gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-fl whitespace-nowrap shrink-0"
                        style={{
                            border: '1px solid color-mix(in srgb, #3ad6ff 40%, transparent)',
                            background:
                                'linear-gradient(135deg, color-mix(in srgb, #8b6dff 18%, transparent), color-mix(in srgb, #3ad6ff 10%, transparent))',
                            boxShadow:
                                '0 0 16px -6px #3ad6ff, inset 0 0 12px -4px color-mix(in srgb, #8b6dff 38%, transparent)',
                        }}
                    >
                        <span
                            className="grid place-items-center shrink-0 rounded-[5px]"
                            style={{
                                width: 15,
                                height: 15,
                                background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                boxShadow: '0 0 9px -1px rgba(58,214,255,0.75)',
                            }}
                        >
                            <span className="font-black leading-none text-white" style={{ fontSize: 9 }}>
                                $
                            </span>
                        </span>
                        <span
                            className="font-chat-nick text-[13.5px] font-extrabold tabular-nums"
                            style={{
                                background: 'linear-gradient(135deg, #b7a6ff, #5fe0ff)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            {game.priceUsd.toFixed(2)}
                        </span>
                    </span>
                    <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3.5 h-3.5 shrink-0"
                        style={{ color: modeColor, filter: `drop-shadow(0 0 5px ${modeColor})` }}
                    >
                        {cursed ? (
                            <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
                        ) : (
                            <path d="M12 2l2.6 6.9 7.4.3-5.8 4.6 2 7.2L12 17l-6.2 4 2-7.2L2 9.2l7.4-.3z" />
                        )}
                    </svg>
                    <span className="font-chat-nick text-[12.5px] font-bold text-text_main truncate">
                        {modeLabel}
                    </span>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                    <BasePlayers
                        game={game}
                        placeholder={() => <GameBattleAvatarPlaceholder />}
                    />
                </div>
            </div>

            {/* MIDDLE — crate strip */}
            <GameBattleCasesList game={game} />

            {/* RIGHT — rounds + action */}
            <div
                className="flex items-center gap-3 max-md:justify-between"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="text-[12px] text-text_3 whitespace-nowrap">
                    {game.status === 'STARTED' ? (
                        <>
                            <b className="font-chat-nick text-white font-extrabold tabular-nums">
                                {openedRounds}
                            </b>
                            <span className="text-text_4">/{totalRounds}</span>
                        </>
                    ) : (
                        <>
                            <b className="font-chat-nick text-text_main font-extrabold tabular-nums">
                                {totalRounds}
                            </b>{' '}
                            rounds
                        </>
                    )}
                </span>
                {renderAction()}
            </div>
        </div>
    );
});

const ActionButton = ({
    onClick,
    children,
    accent,
}: {
    onClick: () => void;
    children: React.ReactNode;
    accent?: boolean;
}) => (
    <button
        onClick={onClick}
        className={`font-chat-nick inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-extrabold tracking-[0.08em] uppercase whitespace-nowrap min-w-[88px] transition-all ${
            accent
                ? 'text-[#04150c] hover:-translate-y-0.5 hover:brightness-[1.06]'
                : 'bg-layer_2 border border-stroke text-text_2 hover:bg-layer_3 hover:border-stroke_strong hover:text-text_main hover:-translate-y-px'
        }`}
        style={
            accent
                ? {
                      background: 'linear-gradient(135deg, #7df3b4, #46e08a 45%, #1f9f7a)',
                      boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.4), 0 0 20px -4px rgba(70,224,138,0.7), 0 6px 16px -6px rgba(31,159,122,0.55)',
                  }
                : undefined
        }
    >
        {children}
    </button>
);

export default GameBattle;
